import {jwtDecode} from 'jwt-decode';
import {getMe, postAuthTokenExchange} from '@di-shared/api/cci/auth';
import {setCciApiRequestHeaderInterceptor, setCciBaseUrl} from '@di-shared/api/cci/httpClient';
import {TokenExchangeParams} from '@di-shared/api/cci/types';
import {setDiApiRequestHeaderInterceptor} from '@di-shared/api/di/axiosInstance';
import {APP_NAME, RETRY_COUNT} from '@di-shared/const';
import {getOneTrustScript, getGoogleTagManagerScript} from '@di-shared/features/analytics/getAnalyticsScript';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {i18nInit} from '@di-shared/i18init';
import {getDatadogScript, setDatadogUser} from '@di-shared/utils/datadog';
import loadScript from '@di-shared/utils/loadScript';
import {tryWithRetry} from '@di-shared/utils/retry';
import {BrandType, RegionType} from '@shared/cci/features/const/environment';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getCookie, setCookie} from '@shared/cookieUtil';
import {checkExcludeBrand, checkIsNotProduction} from './utils/whitelist';

const {getState, setState} = environmentStore;

const REGION_CCI_API_BASE = {
  [RegionType.KR]: import.meta.env.VITE_CCI_KR_API_URL!,
  [RegionType.EU]: import.meta.env.VITE_CCI_EU_API_URL!,
};

const BRAND = (import.meta.env.VITE_BRAND as BrandType).toLowerCase();

const SERVICE_TYPE: {[key in RegionType]: TokenExchangeParams['serviceType']} = {
  [RegionType.KR]:
    import.meta.env.VITE_BRAND === BrandType.KIA
      ? 'MY_KIA'
      : import.meta.env.VITE_BRAND === BrandType.HYUNDAI
        ? 'MY_HYUNDAI'
        : 'MY_GENESIS',
  [RegionType.EU]: 'DI',
};

async function wrappingWindowLikeNativeApp() {
  const wrapping = await import('@cci-shared/features/webviewInterfaceWrapper');
  wrapping.default(window);
}

export async function defaultInit() {
  getState().initEnvironment(APP_NAME, window.navigator.userAgent);

  // mock
  if (import.meta.env.VITE_MOCK === 'true') {
    await import('./api/cci/mock');
    await import('./api/di/mock');
  }

  // vconsole
  if (import.meta.env.VITE_PROFILE !== 'prod') {
    const {default: VConsole} = await import('vconsole');
    new VConsole();
  }

  // sciprt 동적 호출
  // OneTrust -> GTM 순서 유지 필수
  // 1. EU Kia만 호출
  // 2. (TODO) EU kia prod 환경 스크립트 추가
  if (getState().region === RegionType.EU && checkIsNotProduction() && checkExcludeBrand(BrandType.GENESIS, BrandType.HYUNDAI)) {
    loadScript(getOneTrustScript(import.meta.env.VITE_ONE_TRUST_ID, import.meta.env.VITE_PROFILE === 'prod'));
  }
  // gtm 동적 호출
  // 1. 검증계나 운영계
  // 2. (TODO) EU Kia 추가
  if (import.meta.env.VITE_PROFILE === 'stg' || import.meta.env.VITE_PROFILE === 'prod') {
    const gtmId = getState().region === RegionType.KR ? import.meta.env.VITE_GTM_KR_ID : import.meta.env.VITE_GTM_EU_ID;
    const newGtmId = import.meta.env.VITE_GTM_ID;
    loadScript(getGoogleTagManagerScript(gtmId));
    loadScript(getGoogleTagManagerScript(newGtmId));
  }
  // datadog 동적 호출
  if (import.meta.env.VITE_DATADOG_PROFILE === 'stg' || import.meta.env.VITE_DATADOG_PROFILE === 'prod') {
    loadScript(getDatadogScript(getState().region));
  }

  // markup data-region 주입
  if (getState().region === RegionType.EU) {
    document.body.setAttribute('data-region', 'EU');
  }

  let cciAccessToken;
  const splitPath = window.location.pathname.split('/');
  const queryString = new URLSearchParams(window.location.search);
  const vin = queryString.get('vin');

  if (!getState().isWebview) {
    if (import.meta.env.VITE_PROFILE === 'prod') {
      // token을 가지고 있는 상태에서 auth 경로로 접근한 경우 sessionStorage 저장 & 홈으로 리다이렉션 처리
      cciAccessToken = queryString.get('cciAccessToken');
      if (splitPath.includes('voc-auth') && cciAccessToken) {
        sessionStorage.setItem('cciAccessToken', cciAccessToken);
        window.location.href = `https://di.${BRAND}.com/${vin}/home`;
        return;
      }
      cciAccessToken = window.sessionStorage.getItem('cciAccessToken');
      // 운영 환경에서 cciToken을 가지고 있지 않다면 리다이렉션 처리
      if (!cciAccessToken) {
        window.location.href = `https://${BRAND}.com`;
        return;
      }
    }
    // prod 가 아니고, 모바일 모드라면 래퍼 on
    await wrappingWindowLikeNativeApp();
  }

  setCciBaseUrl(REGION_CCI_API_BASE[getState().region]);
  await setApiRequestHeaderInterceptor();
  await i18nInit(getState().lang, BRAND, getState().region);
  setCookie('region', getState().region.toLowerCase());
  setCookie('locale', getState().locale.toLowerCase());
  setCookie('brand', import.meta.env.VITE_BRAND.toLowerCase());

  // 2. cci user 정보 (puid, sub, ccid) 획득
  try {
    const {puid, sub} = await tryWithRetry(async () => await getMe(), RETRY_COUNT);
    const ccid = await ccWebviewUtils.getCcid();
    // FIXME : theme 미디어 쿼리 쓰게끔 변경
    // const {matches} = window.matchMedia('(prefers-color-scheme: dark)');
    // setState({puid, sub, ccid, theme: matches ? ThemeType.DARK : ThemeType.LIGHT});
    setState({puid, sub, ccid});
  } catch (e) {
    ccWebviewUtils.closeContent();
  }
}

async function setApiRequestHeaderInterceptor() {
  setCciApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
    };
    // 1. cci AccessToken 획득
    let cciAccessToken: string = '';
    cciAccessToken = window.sessionStorage.getItem('cciAccessToken') || '';
    if (!cciAccessToken) {
      if (import.meta.env.VITE_PROFILE !== 'local') {
        cciAccessToken = await ccWebviewUtils.getAuthToken();
      } else {
        cciAccessToken = import.meta.env.VITE_ACCESS_TOKEN || getCookie('di-ac');
      }
    }

    if (cciAccessToken) {
      headers.Authorization = `Bearer ${cciAccessToken}`;
    }

    return headers;
  });

  setDiApiRequestHeaderInterceptor(async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Accept-Language': getState().lang.includes('-') ? `${getState().lang}` : `${getState().lang}-${getState().locale}`,
      'Client-Timezone': getState().timezone,
    };

    // 3. cci AccessToken 획득
    let cciAccessToken: string = '';
    cciAccessToken = window.sessionStorage.getItem('cciAccessToken') || '';
    if (!cciAccessToken) {
      if (import.meta.env.VITE_PROFILE !== 'local') {
        cciAccessToken = await ccWebviewUtils.getAuthToken();
      } else {
        cciAccessToken = import.meta.env.VITE_ACCESS_TOKEN || getCookie('di-ac');
      }
    }

    // 4. di AccessToken 획득
    try {
      const {accessToken: diAccessToken} = await tryWithRetry(
        async () =>
          await postAuthTokenExchange({
            token: cciAccessToken as string,
            serviceType: SERVICE_TYPE[getState().region],
          }),
        RETRY_COUNT
      );
      if (cciAccessToken) {
        try {
          const {uid} = jwtDecode<{uid: string}>(cciAccessToken);
          setDatadogUser(uid);
        } catch (error) {
          console.error('Failed to decode JWT token', error);
        }
        headers['Cci-Access-Token'] = cciAccessToken;
      }
      if (diAccessToken) {
        headers.Authorization = `Bearer ${diAccessToken}`;
      }
    } catch (e) {
      ccWebviewUtils.closeContent();
    }

    return headers;
  });
}
